import React from "react";
import { Router, Link, useLocation } from "@reach/router";
import css from "classnames";
import { useQuery } from "@apollo/client";
import { get, endsWith } from "lodash";
import { Feature } from "types/gql-types";
import { useFeature } from "components/feature";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import { Viewer } from "components/auth/types";
import { ListSettingsSubPage } from "./lists/list-settings";
import StatusesSettings from "./stages/settings";
import MembersSettings from "./stages/members";
import BuildingSettings from "./buildings/building-list";
import FundingSources from "./funding";
import { LIST } from "./queries";
import RentLimits from "./rent-limits";

interface Props {
  listId?: string;
  path: string;
  viewer: Viewer;
}
const NavTabs:React.FC<{listId: string}> = ({ listId }) => {
  const location = useLocation();
  const buildingsEnabled = useFeature(Feature.BUILDING_SETTINGS);
  const fundingEnabled = useFeature(Feature.FUNDING_SOURCES);
  const currentPath = location.pathname;
  return (<ul className={css("nav", "nav-tabs")}>
    <li role="presentation" className={css({ active: endsWith(currentPath, "lists") })}>
      <Link to={`/dash/manager/settings/property/${listId}/lists`}>Lists</Link>
    </li>
    <li role="presentation" className={css({ active: endsWith(currentPath, "statuses") })}>
      <Link to={`/dash/manager/settings/property/${listId}/statuses`}>Statuses</Link>
    </li>
    <li role="presentation" className={css({ active: endsWith(currentPath, "members") })}>
      <Link to={`/dash/manager/settings/property/${listId}/members`}>Team Members</Link>
    </li>
    {buildingsEnabled && (
      <li role="presentation" className={css({ active: endsWith(currentPath, "buildings") })}>
        <Link to={`/dash/manager/settings/property/${listId}/buildings`}>Building Settings</Link>
      </li>)}
    {fundingEnabled && (
      <li role="presentation" className={css({ active: endsWith(currentPath, "funding") })}>
        <Link to={`/dash/manager/settings/property/${listId}/funding`}>Funding Sources</Link>
      </li>)}
    {buildingsEnabled && (
      <li role="presentation" className={css({ active: endsWith(currentPath, "rent-limits") })}>
        <Link to={`/dash/manager/settings/property/${listId}/rent-limits`}>Rent Limits</Link>
      </li>)}
  </ul>);
};
const PropertySettingsPage: React.FC<Props> = (props) => {
  const { listId, viewer } = props;
  const { data, loading, error } = useQuery(LIST, {
    fetchPolicy: "cache-first",
    variables: { id: listId },
  });

  if (!listId) return <ServerError desc="Missing Property ID" />;
  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return <ServerError desc="Missing Data" />;

  const list = get(data, "interestList", null);
  return (
    <div>
      <div style={{ height: "100%", width: "100%" }}>
        <h2 className="margin-bottom-l">{ list?.name } Property Settings</h2>
        <NavTabs listId={listId} />
        <div>
          <Router primary={false}>
            <ListSettingsSubPage path="lists" listId={listId} propertyId={list.property.id} viewer={viewer}/>
            <StatusesSettings path="statuses" listId={listId} propertyId={list.property.id} viewer={viewer}/>
            <MembersSettings path="members" listId={listId} isAdmin={viewer.isAdmin} isSupervisor={viewer.isSupervisor}/>
            <BuildingSettings path="buildings" propertyId={list.property.id} isAdmin={viewer.isAdmin} isSupervisor={viewer.isSupervisor}/>
            <FundingSources path="funding" propertyId={list.property.id} isAdmin={viewer.isAdmin} isSupervisor={viewer.isSupervisor}/>
            <RentLimits path="rent-limits" propertyId={list.property.id} isAdmin={viewer.isAdmin} isSupervisor={viewer.isSupervisor}/>
          </Router>
        </div>
      </div>
    </div>);
};

export default PropertySettingsPage;
